import type { CuentaMpVista, TerminalVista } from '@barbones/shared';
import { useQuery } from '@tanstack/react-query';
import { api, ErrorApi } from '@/api/cliente';

/** Mensajes en español por código de `?error=`; cualquier otro valor usa el genérico. */
const MENSAJES_ERROR: Readonly<Record<string, string>> = {
  OAUTH_ESTADO_INVALIDO: 'La solicitud de conexión expiró o ya se usó. Vuelve a pulsar “Conectar Mercado Pago”.',
  OAUTH_CANCELADO: 'Cancelaste la autorización en Mercado Pago. La cuenta no se conectó.',
  MP_NO_DISPONIBLE: 'Mercado Pago no está disponible en este momento. Intenta de nuevo en unos minutos.',
  MP_CUENTA_REVOCADA: 'La autorización de la cuenta de Mercado Pago fue revocada. Vuelve a conectarla.',
  MP_NO_CONECTADO: 'Mercado Pago no está configurado en este servidor.',
  MP_SIN_PERMISO: 'Mercado Pago rechazó la operación por falta de permisos. Revisa la configuración de la cuenta o de la terminal en Mercado Pago.',
  TERMINAL_EN_USO: 'Hay un cobro pendiente en una terminal. Espera a que termine e intenta de nuevo.',
};
const MENSAJE_GENERICO = 'No se pudo conectar Mercado Pago. Intenta de nuevo.';

/** Traduce un código; nunca devuelve texto proveniente de la URL. */
export function mensajeParaCodigo(codigo: unknown): string {
  return typeof codigo === 'string' && Object.hasOwn(MENSAJES_ERROR, codigo) ? MENSAJES_ERROR[codigo]! : MENSAJE_GENERICO;
}

/** Solo se navega a la autorización de Mercado Pago (https, host exacto, sin credenciales). */
export function urlAutorizacionSegura(valor: unknown): string | null {
  if (typeof valor !== 'string') return null;
  try {
    const u = new URL(valor);
    const ok = u.protocol === 'https:' && u.hostname === 'auth.mercadopago.com' && u.port === '' && !u.username && !u.password;
    return ok ? u.toString() : null;
  } catch {
    return null;
  }
}

/** El módulo está apagado en el servidor (o la cuenta aún no existe para operar). */
export const esModuloNoConfigurado = (e: unknown): boolean => e instanceof ErrorApi && e.status === 409 && e.code === 'MP_NO_CONECTADO';

export function useCuentaMp(sucursalId: string | null) {
  return useQuery({
    queryKey: ['mp-cuenta', sucursalId],
    queryFn: () => api<CuentaMpVista>(`/mercadopago/sucursales/${sucursalId}/cuenta`),
    enabled: !!sucursalId,
    retry: (n, e) => !esModuloNoConfigurado(e) && n < 2,
  });
}

export function useTerminalesMp(sucursalId: string | null, habilitado = true) {
  return useQuery({
    queryKey: ['mp-terminales', sucursalId],
    queryFn: () => api<TerminalVista[]>(`/mercadopago/sucursales/${sucursalId}/terminales`),
    enabled: !!sucursalId && habilitado,
  });
}
